import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { 
  MessageCircle, 
  ThumbsUp, 
  ThumbsDown, 
  Trophy, 
  Target, 
  Crown,
  Flame,
  Award,
  Calendar,
  Users,
  TrendingUp
} from "lucide-react";
import { AnimatedBackground } from "./AnimatedBackground";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface Comment {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  emoji_reactions: any;
  profiles?: {
    display_name: string;
    avatar_url?: string;
  };
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  challenge_type: string;
  start_date: string;
  end_date: string;
  prize_pool: number;
  entry_fee: number;
  current_participants: number;
  max_participants: number;
}

interface Badge {
  id: string;
  badge_name: string;
  description: string;
  badge_type: string;
  earned_at: string;
}

interface CommunityFeaturesProps {
  tipId?: string;
  showComments?: boolean;
  showChallenges?: boolean;
  showBadges?: boolean;
}

export function CommunityFeatures({ 
  tipId, 
  showComments = true, 
  showChallenges = true, 
  showBadges = true 
}: CommunityFeaturesProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [userBadges, setUserBadges] = useState<Badge[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (showComments && tipId) fetchComments();
    if (showChallenges) fetchChallenges();
    if (showBadges && user) fetchUserBadges();
  }, [tipId, user, showComments, showChallenges, showBadges]);

  const fetchComments = async () => {
    if (!tipId) return;
    
    try {
      const { data, error } = await supabase
        .from('tip_comments')
        .select('*')
        .eq('tip_id', tipId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch profile data separately to avoid relationship issues
      const commentsWithProfiles = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name, avatar_url')
            .eq('user_id', comment.user_id)
            .single();
          
          return {
            ...comment,
            profiles: profile
          };
        })
      );
      
      setComments(commentsWithProfiles);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchChallenges = async () => {
    try {
      const { data, error } = await supabase
        .from('tipster_challenges')
        .select('*')
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .order('start_date', { ascending: true })
        .limit(3);

      if (error) throw error;
      setChallenges(data || []);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const fetchUserBadges = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', user.id)
        .order('earned_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setUserBadges(data || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async () => {
    if (!user || !tipId || !newComment.trim()) return;

    try {
      const { error } = await supabase
        .from('tip_comments')
        .insert({
          tip_id: tipId,
          user_id: user.id,
          content: newComment.trim()
        });

      if (error) throw error;

      setNewComment("");
      fetchComments();
      toast({
        title: "Comment posted!",
        description: "Your comment has been added successfully.",
      });
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const addReaction = async (commentId: string, emoji: string) => {
    if (!user) return;

    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return;

      const reactions = { ...comment.emoji_reactions };
      reactions[emoji] = (reactions[emoji] || 0) + 1;

      const { error } = await supabase
        .from('tip_comments')
        .update({ emoji_reactions: reactions })
        .eq('id', commentId);

      if (error) throw error;
      fetchComments();
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const getChallengeTypeIcon = (type: string) => {
    switch (type) {
      case 'weekly_roi': return <TrendingUp className="w-4 h-4" />;
      case 'win_streak': return <Flame className="w-4 h-4" />;
      case 'sport_specific': return <Target className="w-4 h-4" />;
      default: return <Trophy className="w-4 h-4" />;
    }
  };

  const getBadgeIcon = (type: string) => {
    switch (type) {
      case 'streak': return <Flame className="w-4 h-4" />;
      case 'purchase': return <Crown className="w-4 h-4" />;
      case 'referral': return <Users className="w-4 h-4" />;
      case 'achievement': return <Award className="w-4 h-4" />;
      default: return <Trophy className="w-4 h-4" />;
    }
  };

  return (
    <AnimatedBackground variant="minimal">
      <div className="space-y-6">
        {/* User Badges Section */}
        {showBadges && user && userBadges.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Your Achievements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {userBadges.map((badge) => (
                  <Badge key={badge.id} variant="outline" className="flex items-center gap-1 p-2">
                    {getBadgeIcon(badge.badge_type)}
                    <span className="text-xs">{badge.badge_name}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Challenges Section */}
        {showChallenges && challenges.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Active Challenges
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {challenges.map((challenge) => (
                <div key={challenge.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getChallengeTypeIcon(challenge.challenge_type)}
                      <div>
                        <h4 className="font-semibold">{challenge.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {challenge.description}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      ${challenge.prize_pool} prize pool
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {challenge.current_participants}/{challenge.max_participants}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Ends {new Date(challenge.end_date).toLocaleDateString()}
                      </span>
                    </div>
                    <Button size="sm" variant="outline">
                      Join Challenge
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Comments Section */}
        {showComments && tipId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Community Discussion
                <Badge variant="secondary" className="text-xs">
                  {comments.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Comment */}
              {user && (
                <div className="space-y-3">
                  <Textarea
                    placeholder="Share your thoughts on this tip..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="min-h-[80px]"
                  />
                  <div className="flex justify-end">
                    <Button 
                      onClick={submitComment}
                      disabled={!newComment.trim()}
                      size="sm"
                    >
                      Post Comment
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No comments yet. Be the first to share your thoughts!</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={comment.profiles?.avatar_url} />
                        <AvatarFallback>
                          {comment.profiles?.display_name?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {comment.profiles?.display_name || 'Anonymous'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <p className="text-sm">{comment.content}</p>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => addReaction(comment.id, '👍')}
                          >
                            👍 {comment.emoji_reactions['👍'] || 0}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => addReaction(comment.id, '👎')}
                          >
                            👎 {comment.emoji_reactions['👎'] || 0}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => addReaction(comment.id, '🔥')}
                          >
                            🔥 {comment.emoji_reactions['🔥'] || 0}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AnimatedBackground>
  );
}